/** @jsx jsx */
import { jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { Link as RouterLink } from 'react-router';

import type { SpacingValue } from '@coursera/cds-common';
import { Grid, Hidden, Typography2, useTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

const TrackedRouterLink = withSingleTracked({ type: 'BUTTON' })(RouterLink);

export const NavigationBlock = ({
  icon,
  title,
  description,
  to,
  newTab,
  onClick,
  toTrackingName,
  padding = 12,
  ...props
}: {
  icon?: React.ReactNode;
  title: string;
  description: React.ReactNode;
  to?: string;
  newTab?: boolean;
  onClick?: () => void;
  toTrackingName?: string;
  padding?: SpacingValue;
}) => {
  const theme = useTheme();
  const blockStyle: Interpolation<Theme> = {
    background: 'var(--cds-color-white-0)',
    border: '1px solid ' + 'var(--cds-color-grey-300)',
    borderRadius: '3px',
    padding: theme.spacing(padding),
    display: 'block',
    width: '100%',
    textAlign: 'left',
    textDecoration: 'none !important',
    outline: 'none !important',
    ':hover': { backgroundColor: 'var(--cds-color-blue-50)', borderColor: 'var(--cds-color-blue-700)' },
    ':focus': { backgroundColor: 'var(--cds-color-blue-50)', borderColor: 'var(--cds-color-blue-700)' },
  };

  const layoutWithNoIcon = (
    <Grid container alignItems="center" spacing={24}>
      <Grid item xs={10} sm={11}>
        <Typography2 component="h3" variant="subtitleMedium">
          {title}
        </Typography2>
        <Typography2 component="p" variant="bodyPrimary" color="supportText">
          {description}
        </Typography2>
      </Grid>
      <Grid item xs={2} sm={1} container justifyContent="center">
        <ArrowNextIcon color="default" />
      </Grid>
    </Grid>
  );

  const navigationBlockContent = !icon ? (
    layoutWithNoIcon
  ) : (
    <React.Fragment>
      <Hidden xsDown>
        <Grid container alignItems="center" spacing={24}>
          <Grid item sm={2} md={1} container justifyContent="center">
            {icon}
          </Grid>
          <Grid item sm={9} md={10}>
            <Typography2 component="h3" variant="subtitleMedium">
              {title}
            </Typography2>
            <Typography2 component="p" variant="bodyPrimary" color="supportText">
              {description}
            </Typography2>
          </Grid>
          <Grid item sm={1} container justifyContent="center">
            <ArrowNextIcon color="default" />
          </Grid>
        </Grid>
      </Hidden>
      <Hidden smUp>{layoutWithNoIcon}</Hidden>
    </React.Fragment>
  );

  if (to) {
    if (newTab) {
      return (
        <a href={to} target="_blank" rel="noreferrer" {...props} css={blockStyle}>
          {navigationBlockContent}
        </a>
      );
    } else {
      if (toTrackingName) {
        return (
          <TrackedRouterLink trackingName={toTrackingName} to={to} {...props} css={blockStyle}>
            {navigationBlockContent}
          </TrackedRouterLink>
        );
      }
      return (
        <RouterLink to={to} {...props} css={blockStyle}>
          {navigationBlockContent}
        </RouterLink>
      );
    }
  } else {
    return (
      <button type="button" onClick={onClick} {...props} css={blockStyle}>
        {navigationBlockContent}
      </button>
    );
  }
};
